import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import HotList from "@/components/feature/HotList";
import { useSeo } from "@/hooks/useSeo";
import { posts } from "@/mocks/posts";
import { categories } from "@/mocks/categories";
import type { ContentBlock } from "@/types/content";

export default function PostDetail() {
  const { slug = "" } = useParams();
  const post = posts.find((p) => p.slug === slug);
  const [copied, setCopied] = useState(false);

  const category = categories.find((c) => c.slug === post?.categorySlug);

  const toc = useMemo(
    () =>
      (post?.blocks ?? [])
        .map((block, idx) => ({ block, idx }))
        .filter((item) => item.block.type === "h2")
        .map((item) => ({ id: `section-${item.idx}`, text: item.block.text ?? "" })),
    [post]
  );

  const related = useMemo(
    () => (post ? posts.filter((p) => p.categorySlug === post.categorySlug && p.id !== post.id).slice(0, 4) : []),
    [post]
  );

  useSeo({
    title: post ? `${post.title} - 网曝吃瓜` : "条目不存在 - 网曝吃瓜",
    description: post ? `${post.excerpt}（网曝吃瓜 · 网友投稿整理，来源可溯。）` : "该条目暂不存在或已下线。",
    keywords: post ? `${post.tags.join(",")},网曝吃瓜,吃瓜爆料,网友投稿` : "网曝吃瓜",
    ogType: "article",
    image: post?.cover,
    jsonLd: post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: [post.cover],
            datePublished: post.date,
            dateModified: post.date,
            author: { "@type": "Person", name: post.author },
            publisher: { "@type": "Organization", name: "网曝吃瓜" },
            articleSection: category?.name ?? "综合",
            keywords: post.tags.join(","),
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://wangbaochigua.example/" },
              { "@type": "ListItem", position: 2, name: category?.name ?? "综合" },
              { "@type": "ListItem", position: 3, name: post.title },
            ],
          },
        ]
      : [],
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full px-4 md:px-6 py-20 text-center">
          <i className="ri-file-unknow-line text-5xl text-background-400" aria-hidden="true" />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950">这条线索暂时找不到了</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经被合并到其他条目，或者链接输入有误。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const renderBlock = (block: ContentBlock, idx: number) => {
    switch (block.type) {
      case "h2":
        return (
          <h2
            key={idx}
            id={`section-${idx}`}
            className="scroll-mt-24 mt-8 mb-3 text-xl md:text-2xl font-bold text-foreground-950 border-l-4 border-primary-500 pl-3"
          >
            {block.text}
          </h2>
        );
      case "quote":
        return (
          <blockquote
            key={idx}
            className="my-6 p-4 md:p-5 rounded-lg bg-accent-50 border-l-4 border-accent-500 text-foreground-800 leading-relaxed"
          >
            <i className="ri-double-quotes-l text-accent-600" aria-hidden="true" />
            <span className="ml-1">{block.text}</span>
          </blockquote>
        );
      case "list":
        return (
          <ul key={idx} className="my-4 space-y-2.5">
            {block.items?.map((item) => (
              <li key={item} className="flex gap-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                <i className="ri-checkbox-blank-circle-fill text-[6px] text-primary-500 mt-2 shrink-0" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      default:
        return (
          <p key={idx} className="my-4 text-sm md:text-base text-foreground-700 leading-loose">
            {block.text}
          </p>
        );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb items={[{ label: category?.name ?? "综合", to: `/category/${post.categorySlug}` }, { label: post.title }]} />
        </div>

        <div className="w-full px-4 md:px-6 pb-12 md:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <article className="lg:col-span-8">
            <header>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${post.categorySlug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors"
                >
                  <i className={category?.icon ?? "ri-bookmark-line"} aria-hidden="true" />
                  {category?.name ?? "综合"}
                </Link>
                {post.hot && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-accent-400 text-accent-950 text-xs">
                    <i className="ri-fire-fill" aria-hidden="true" />
                    编辑部热帖
                  </span>
                )}
              </div>

              <h1 className="mt-4 text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">{post.title}</h1>
              <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">{post.excerpt}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500 border-y border-background-200 py-3">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line" aria-hidden="true" />
                  {post.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true" />
                  {post.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                  {post.heat} 热度
                </span>
              </div>
            </header>

            <figure className="mt-6">
              <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden bg-background-200">
                <img
                  src={post.cover}
                  alt={post.alt}
                  title={`${post.title} - 网曝吃瓜爆料`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-xs text-foreground-400 text-center">
                配图为栏目示意插画，不代表事件现场画面
              </figcaption>
            </figure>

            <div className="mt-6">{post.blocks.map((block, idx) => renderBlock(block, idx))}</div>

            <div className="mt-8 p-4 rounded-lg bg-secondary-50 border border-secondary-100 flex gap-3">
              <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-secondary-500 text-background-50">
                <i className="ri-information-line" aria-hidden="true" />
              </span>
              <p className="text-sm text-secondary-900 leading-relaxed">
                本文内容来自网友投稿整理，编辑部已对可见细节进行交叉比对，但无法保证全部信息绝对准确。
                请理性围观，具体事实以当事方正式说明为准。
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">标签：</span>
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1 rounded-md bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className={copied ? "ri-check-line" : "ri-link"} aria-hidden="true" />
                {copied ? "链接已复制" : "复制链接"}
              </button>
              <Link
                to="/submit"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-line" aria-hidden="true" />
                补充线索
              </Link>
            </div>

            {related.length > 0 && (
              <section aria-labelledby="related-title" className="mt-10 pt-8 border-t border-background-200">
                <h2 id="related-title" className="text-xl font-bold text-foreground-950 mb-5">
                  同栏目相关整理
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/post/${item.slug}`}
                      className="group p-4 rounded-lg border border-background-200 hover:border-primary-300 transition-colors"
                    >
                      <span className="text-xs text-secondary-700">{category?.name}</span>
                      <h3 className="mt-1.5 text-sm font-bold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {item.title}
                      </h3>
                      <span className="mt-2 block text-xs text-foreground-400">{item.date}</span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 flex flex-col gap-6">
              {toc.length > 0 && (
                <nav aria-labelledby="toc-title" className="bg-background-50 border border-background-200 rounded-lg p-4">
                  <h2 id="toc-title" className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
                    <i className="ri-list-check-2 text-primary-500" aria-hidden="true" />
                    本文目录
                  </h2>
                  <ol className="space-y-2">
                    {toc.map((item, i) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="flex gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                        >
                          <span className="text-foreground-400">{String(i + 1).padStart(2, "0")}</span>
                          <span className="line-clamp-2">{item.text}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}

              <HotList posts={[...posts].sort((a, b) => b.heat - a.heat)} limit={8} title="大家都在看" />
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}